import { Link, useLocation, useNavigate } from 'react-router-dom'
import { resolveNotificationDestination } from '@domain/features/user-notifications/user-notifications-feature'
import { UserNotificationType, type UserNotification } from '@domain/models/user-notifications'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useUserDisplayNames } from '@presentation/hooks/useUserDisplayNames'
import { useUserNotifications } from '@presentation/hooks/useUserNotifications'
import SeoHead from '@presentation/seo/SeoHead'

const ALLOWED_WEB_NOTIFICATION_TYPES = new Set<string>(Object.values(UserNotificationType))

function formatNotificationDate(value: Date | undefined): string {
  if (!value) {
    return ''
  }
  return value.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function resolveNotificationTitle(
  notification: UserNotification,
  actorName?: string,
): string {
  const title = notification.title?.trim()
  if (title) {
    const canReplaceActorPlaceholder = actorName
      && actorName !== 'Unknown user'
      && actorName !== 'System'
      && !actorName.startsWith('User ')
    if (canReplaceActorPlaceholder && /\bsomeone\b/i.test(title)) {
      return title.replace(/\bsomeone\b/gi, actorName)
    }
    return title
  }
  return 'Activity update'
}

function resolveNotificationDescription(notification: UserNotification): string {
  const description = notification.description?.trim()
  if (description) {
    return description
  }
  return 'Open to view the latest activity on your content.'
}

export default function UserNotificationsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuthUser()
  const {
    notifications,
    loading,
    error,
    isAuthenticated,
    markAsRead,
    isMarkingAsRead,
  } = useUserNotifications()
  const webNotifications = notifications.filter((notification) =>
    Boolean(
      notification.notificationType
      && ALLOWED_WEB_NOTIFICATION_TYPES.has(notification.notificationType),
    ))
  const senderIds = webNotifications.map((item) => item.sender)
  const { resolveDisplayName } = useUserDisplayNames(senderIds)

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const handleOpenNotification = async (notification: UserNotification) => {
    if (!notification.uid) {
      return
    }

    const destination = resolveNotificationDestination(notification)

    if (!notification.read) {
      await markAsRead(notification.uid)
    }

    navigate({
      pathname: destination.pathname,
      search: destination.search,
    })
  }

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      <SeoHead
        title=" Notifications | Parkinson's Insights"
        description="Latest activity for your account."
        canonicalPath="/notifications"
        noindex
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>

        <div className="mt-5 ml-1 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
            Notifications
          </h1>
          <p className="mt-2 text-base font-semibold text-text-secondary">
            This is the latest activity that happens to the user.
          </p>
        </div>

        {loading ? (
          <LoadingSpinner className="mt-8" />
        ) : !isAuthenticated || !user ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              Log in to see your notifications.
            </p>
            <button
              type="button"
              onClick={openLoginModal}
              className="mt-4 rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-light"
            >
              Log In
            </button>
          </div>
        ) : error ? (
          <p className="mt-8 text-center text-text-secondary">
            Unable to load notifications. Please try again later.
          </p>
        ) : webNotifications.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              You have no notifications yet.
            </p>
            <p className="mt-1 text-sm text-text-muted">
              Activity on your comments, webinar topics, and hacks will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-4">
            {webNotifications.map((notification) => {
              const senderDisplayName = notification.sender
                ? resolveDisplayName(notification.sender)
                : 'System'

              return (
                <button
                  key={notification.uid}
                  type="button"
                  onClick={() => {
                    void handleOpenNotification(notification)
                  }}
                  disabled={isMarkingAsRead(notification.uid)}
                  className="relative w-full rounded-xl border border-border-light bg-card-surface p-4 text-left shadow-lg shadow-black/20 transition-colors hover:border-blue/40 disabled:cursor-not-allowed disabled:opacity-80"
                >
                  {!notification.read ? (
                    <span
                      className="absolute right-4 top-4 inline-flex h-2.5 w-2.5 rounded-full bg-red-500"
                      aria-hidden
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="text-base font-bold text-text-primary sm:text-lg">
                      {resolveNotificationTitle(notification, senderDisplayName)}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-text-secondary">
                      {resolveNotificationDescription(notification)}
                    </p>
                    <p className="mt-3 text-xs font-semibold text-text-muted">
                      {senderDisplayName}
                      {notification.createdTime ? ` · ${formatNotificationDate(notification.createdTime)}` : ''}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
